import React, { useEffect, useRef } from 'react';

/**
 * SolvibeLogo — decorative parallax logo overlay
 *
 * • Starts appearing at the "What is SolVibe?" section (after the Hero)
 * • Fades in smoothly, grows larger as you scroll down
 * • Always RIGHT side, never overlaps real content (z-index 1, pointer-events none)
 * • Uses the real SolVibe SVG logo (/logos/SolVibe-Logo.svg)
 */

const VIBIEMoon = () => {
  const wrapRef = useRef(null);
  const rafRef  = useRef(null);

  useEffect(() => {
    const wrap = wrapRef.current;
    if (!wrap) return;

    // ── Start point: "What is SolVibe?" begins right after the Hero section.
    //    Hero fills ~1 viewport. We start showing the logo from there.
    //    Recalculate on resize so it's always accurate.
    let startY = window.innerHeight;
    const recalcStart = () => {
      startY = window.innerHeight * 0.88;
    };
    recalcStart();

    const update = () => {
      const scrollY = window.scrollY;
      const vw      = window.innerWidth;
      const vh      = window.innerHeight;
      const rawPageH = document.documentElement.scrollHeight - window.innerHeight;
      const pageH   = Math.max(1, rawPageH);

      // Invisible while the Hero is visible
      if (scrollY < startY) {
        wrap.style.opacity = '0';
        return;
      }

      // Smooth fade-in over first 250 px after the section starts
      const fadeIn   = Math.min((scrollY - startY) / 250, 1);

      // Progress: 0 at "What is SolVibe?", 1 at the very bottom of the page
      const rangeY   = Math.max(1, pageH - startY);
      const progress = Math.min((scrollY - startY) / rangeY, 1);

      // ── Size: small/far → large/close ──────────────────────────────────
      const isMobile = vw < 768;
      const minSize  = isMobile ?  85 : 140;
      const maxSize  = isMobile ? 260 : 540;
      const size     = minSize + progress * (maxSize - minSize);

      // ── Opacity: 60% more transparent (baseOpa/topOpa × 0.4) ────────────
      const baseOpa = isMobile ? 0.05 : 0.06;
      const topOpa  = isMobile ? 0.18 : 0.22;
      const opacity = fadeIn * (baseOpa + progress * (topOpa - baseOpa));

      // ── Horizontal: RIGHT side ──────────────────────────────────────────
      // 80 % visible at top → 90 % visible at bottom (nearly fully on screen)
      const visFrac = 0.80 + progress * 0.10;
      const left    = vw - size * visFrac;

      // ── Vertical: center drifts slightly downward ───────────────────────
      const centerY = vh * (0.22 + progress * 0.20);
      const top     = centerY - size / 2;

      wrap.style.opacity = String(opacity);
      wrap.style.width   = size + 'px';
      wrap.style.height  = size + 'px';
      wrap.style.left    = left + 'px';
      wrap.style.top     = top  + 'px';
    };

    const onScroll = () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      rafRef.current = requestAnimationFrame(update);
    };

    const onResize = () => {
      recalcStart();
      update();
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', onResize,  { passive: true });

    // Initial paints — re-run after lazy sections inflate the page height
    update();
    const t1 = setTimeout(update, 300);
    const t2 = setTimeout(update, 1000);

    return () => {
      window.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      clearTimeout(t1);
      clearTimeout(t2);
    };
  }, []);

  return (
    <div
      ref={wrapRef}
      aria-hidden="true"
      style={{
        position:      'fixed',
        top:           0,
        left:          0,
        pointerEvents: 'none',
        zIndex:        1,
        opacity:       0,
        willChange:    'left, top, width, height, opacity',
      }}
    >
      {/* 44KB WebP instead of the 1.3MB SVG — decorative element, identical look at this size */}
      <img
        src="/logos/SolVibe-Logo-512.webp"
        alt=""
        loading="lazy"
        decoding="async"
        draggable={false}
        style={{
          width:     '100%',
          height:    '100%',
          display:   'block',
          objectFit: 'contain',
          /* Neon glow matching the SolVibe brand colours */
          filter: [
            'drop-shadow(0 0 25px rgba(20, 241, 149, 0.30))',
            'drop-shadow(0 0 70px rgba(153, 69, 255, 0.22))',
            'brightness(1.08)',
          ].join(' '),
        }}
      />
    </div>
  );
};

export default VIBIEMoon;
